import * as React from 'react';
import './DetailPageLayout.scss';
import {
  Drawer,
  Icon,
  IBreadcrumbItem,
  Breadcrumb,
  Heading,
  HeadingType,
  ComponentType,
  Button,
  Grid,
  useModal,
  Tooltip,
  Loader,
  RecordsNotFound,
  Scrollbar,
} from '@gcl/react-core/';
import { EFloatingButtonPosition, FloatingButton } from '@core/FloatingButton';
import { TableContentLoader } from '@common/TableContentLoader';
import { useBreakPoints } from '@common/breakpoints.hooks';
import { useDrawerOpenState } from '@common/pageLayout.hooks';
import { EUserProfilePreferenceAttributeName } from '@userProfile/userProfilePreferences.constants';
import { NoAccessRight } from '@common/NoAccessRight';
import { AxiosErrorResponse } from '@core/error.models';

const drawerWidthSmall = '90%';
const drawerWidthLarge = '300px';

export const createDefaultDetailPageTitleFn = (title: string | React.ReactElement) => {
  return (DetailPageRightMenuOpener?: React.ReactElement, InstructionModalOpener?: React.ReactElement) => {
    return (
      <Grid container alignItems="center" justifyContent="space-between" spacing={1} flexWrap="nowrap">
        <Grid item container alignItems="center" flexGrow={1}>
          {title}
        </Grid>
        <Grid item>
          <div className="gcl-gems-detail-page-layout-title-button">
            {InstructionModalOpener}
            {DetailPageRightMenuOpener}
          </div>
        </Grid>
      </Grid>
    );
  };
};

export interface IDetailPageLayoutProps {
  isDrawer?: boolean;
  rightMenu?: (setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>) => React.ReactNode;
  render: (DetailPageRightMenuOpener?: React.ReactElement) => React.ReactElement;
  breadcrumb?: IBreadcrumbItem[];
  title?:
    | string
    | ((
        DetailPageRightMenuOpener?: React.ReactElement,
        InstructionModalOpener?: React.ReactElement,
      ) => React.ReactElement);
  titleRightElement?: React.ReactNode;
  isLoading?: boolean;
  data: any;
  isRefetching: boolean;
  isRestricted?: boolean;
  errorGetDetail: AxiosErrorResponse | null;
  isGlobalPage?: boolean;
}

export const DetailPageLayout: React.FC<IDetailPageLayoutProps> = ({
  rightMenu,
  render,
  breadcrumb,
  title,
  titleRightElement,
  isLoading = false,
  data,
  isRefetching,
  isDrawer = true,
  isRestricted = false,
  errorGetDetail,
  isGlobalPage = false,
}) => {
  const isItemNotFound = React.useMemo(() => {
    return errorGetDetail?.response?.status === 404;
  }, [JSON.stringify(errorGetDetail)]);

  const [isDrawerOpen, setIsDrawerOpen] = useDrawerOpenState(
    EUserProfilePreferenceAttributeName.COLLAPSE_T1_QUICK_NAV,
    isDrawer,
  );

  const { isXs, isSm } = useBreakPoints();
  const isMobileSize = isSm || isXs;
  const DetailPageRightMenuOpener = React.useMemo(() => {
    if (!isDrawer) return undefined;

    return isMobileSize ? undefined : (
      <Button
        onClick={() => setIsDrawerOpen((prev) => !prev)}
        type={ComponentType.Secondary}
        suffixIconName="InfoCircle"
      >
        Info
      </Button>
    );
  }, [isMobileSize, isDrawer]);
  const { openPortal, closePortal, renderModal } = useModal();
  const InstructionModalOpener = React.useMemo(() => {
    return isMobileSize ? undefined : (
      <Tooltip overlay="Instructions" id="gcl-gems-detail-page-layout-instruction-modal-opener-tooltip">
        <Icon onClick={openPortal} iconName="JournalRichtext" className="gcl-ic-btn-xxl" />
      </Tooltip>
    );
  }, [openPortal]);

  const Content = (
    <Grid
      sx={[
        {
          transition: 'margin-right 0.5s ease-in-out',
        },
        isDrawer && isDrawerOpen && !isMobileSize ? { marginRight: `calc(${drawerWidthLarge} + 35px)` } : {},
      ]}
      className={`gcl-gems-detailpage-layout-grid-content ${
        isMobileSize ? 'gcl-m-md' : 'gcl-px-md gcl-mx-xl gcl-my-md'
      }`}
    >
      {isRestricted && !isLoading && (
        <>
          <div className="gcl-gems-detail-page-layout-breadcrumb-container">
            {breadcrumb && <Breadcrumb items={breadcrumb} />}
          </div>
          <NoAccessRight />
        </>
      )}
      {!isRestricted && (
        <>
          <div className="gcl-gems-detail-page-layout-breadcrumb-container">
            {breadcrumb && <Breadcrumb items={breadcrumb} />}
            {data && isRefetching ? (
              <>
                <Loader className="gcl-ml-md" small text="Fetching latest data from server" />
              </>
            ) : null}
          </div>

          <Heading as={HeadingType.h1} styledAs={HeadingType.h3} className="gcl-pb-xs gcl-gems-title">
            {typeof title === 'function' ? title(DetailPageRightMenuOpener, InstructionModalOpener) : title}
            {titleRightElement}
          </Heading>
          {data && (
            <>
              {render(DetailPageRightMenuOpener)}
              {isDrawer && isMobileSize && (
                <FloatingButton
                  type={ComponentType.Dark}
                  position={EFloatingButtonPosition.bottomRight}
                  onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                  iconName="InfoLg"
                />
              )}
            </>
          )}
        </>
      )}
      {isItemNotFound && <RecordsNotFound />}
      {!isItemNotFound && !data && isLoading && <TableContentLoader />}
    </Grid>
  );

  return (
    <div className="gcl-gems-detailpage-layout-container">
      {isDrawer && (
        <Drawer
          setOpen={setIsDrawerOpen}
          open={isDrawerOpen}
          PaperProps={{
            style: {
              width: isMobileSize ? drawerWidthSmall : drawerWidthLarge,
            },
          }}
          variant={isMobileSize ? 'temporary' : 'persistent'}
        >
          <Grid>
            <Button
              type={ComponentType.Secondary}
              className="gcl-ml-md gcl-my-xs"
              onClick={() => setIsDrawerOpen(!isDrawerOpen)}
            >
              <Icon iconName="ChevronRight" />
            </Button>
          </Grid>
          {rightMenu?.(setIsDrawerOpen)}
        </Drawer>
      )}
      <Scrollbar>
        {isGlobalPage ? <div className="gcl-gems-global-detailpage-layout-container">{Content}</div> : <>{Content}</>}
      </Scrollbar>
      {isDrawer &&
        renderModal({
          fullscreen: true,
          onRenderBody: () => {
            return (
              <div className="gcl-gems-detail-page-layout-instruction-modal">
                <img
                  src="/images/GEMS_Instructions.jpg"
                  alt="GEMS Instructions"
                  className="gcl-gems-detail-page-layout-instruction-img"
                />
              </div>
            );
          },
        })}
    </div>
  );
};
